import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Image, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router-dom";
import { TreatmentTemplateResponseDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EditorPanel } from "../components/EditorPanel";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { LocalizedFields } from "../components/LocalizedFields";
import { TranslationStatus } from "../components/TranslationStatus";
import { useColumnSearch } from "../components/useColumnSearch";
import { localizationEnabled } from "../config/locales";
import {
    addTreatmentTemplate,
    clearErrorTreatmentTemplate,
    clearImage,
    deleteTreatmentTemplate,
    fetchTreatmentTemplates,
    getTreatmentTemplateImageContent,
    selectTreatmentTemplate,
    startAddTreatmentTemplate,
    startDeleteTreatmentTemplate,
    updateTreatmentTemplate,
    uploadTreatmentTemplateImageContent
} from "../features/treatmentTemplateSlice";
import { treatmentTemplateFieldKeys, treatmentTemplateFields } from "../model/localizedEntities";
import { allLocalizedValues } from "../utils/translations";

const IMAGE_FALLBACK = "data:image/png;base64,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";

export const TreatmentTemplatesPage = () => {

    const dispatch = useAppDispatch()
    const { t } = useTranslation();
    const treatmentTemplates = useAppSelector(state => state.treatmentTemplate.treatmentTemplates);
    const selectedTreatmentTemplate = useAppSelector(state => state.treatmentTemplate.selectedTreatmentTemplate);
    const status = useAppSelector(state => state.treatmentTemplate.status);
    const error = useAppSelector(state => state.treatmentTemplate.error);
    const imagesCache = useAppSelector(state => state.treatmentTemplate.imagesCache);
    const params = useParams();
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        dispatch(fetchTreatmentTemplates());
    }, []);

    const columnSearch = useColumnSearch<TreatmentTemplateResponseDto>({
        placeholder: t("placeholder.searchTreatment"),
        getSearchValues: template => allLocalizedValues(template, treatmentTemplateFieldKeys)
    });

    if (params.treatmentTemplateId !== undefined) {
        return <Outlet />
    }

    const onRow = (treatmentTemplate: TreatmentTemplateResponseDto) => ({
        onClick: () => {
            dispatch(selectTreatmentTemplate(treatmentTemplate))
        },
    });

    const columns: ColumnsType<TreatmentTemplateResponseDto> = [
        {
            title: t("column.name"),
            dataIndex: 'name',
            key: 'name',
            ...columnSearch,
            sorter: (a, b) => a.name! > b.name! ? -1 : 1,
            ellipsis: true
        },
        ...(localizationEnabled ? [{
            width: 200,
            title: t("column.translations"),
            key: 'translations',
            render: (_: unknown, template: TreatmentTemplateResponseDto) =>
                <TranslationStatus entity={template} fields={treatmentTemplateFieldKeys} />
        }] : [])
    ];

    const getRowClassName = (treatmentTemplate: TreatmentTemplateResponseDto) => {
        return selectedTreatmentTemplate != null && selectedTreatmentTemplate.id === treatmentTemplate.id ? 'bg-selected' : '';
    }

    const onSaveTreatmentTemplate = () => {
        if (selectedTreatmentTemplate?.name == null) return;

        if (selectedTreatmentTemplate.id == null) {
            dispatch(addTreatmentTemplate(selectedTreatmentTemplate));
            return;
        }

        dispatch(updateTreatmentTemplate(selectedTreatmentTemplate));
    }

    const onEditTreatmentTemplate = () => {
        if (selectedTreatmentTemplate == null) return;

        // Cache is keyed by image id, so that is what the hit has to be tested
        // against; templates without an image have nothing to fetch.
        const imageId = selectedTreatmentTemplate.imageMediaFileId;
        if (imageId != null && imagesCache[imageId] == null) {
            dispatch(getTreatmentTemplateImageContent({ id: selectedTreatmentTemplate.id!, imageId }));
        }
        dispatch(startAddTreatmentTemplate(selectedTreatmentTemplate));
    }

    const handleFileChange = (e: any) => {
        const [file] = e.target.files;
        dispatch(uploadTreatmentTemplateImageContent(file));
    }

    const canSave = selectedTreatmentTemplate?.name != null
        && selectedTreatmentTemplate.name.trim().length > 0;

    const currentImage = selectedTreatmentTemplate?.imageMediaFileId != null
        ? imagesCache[selectedTreatmentTemplate.imageMediaFileId]
        : undefined;

    const changeImageButton = selectedTreatmentTemplate?.imageMediaFileId != null
        ? <Space>
            <Button type="primary" onClick={() => fileRef.current?.click()}>{t("actions.change")}</Button>
            <Button
                type="primary"
                danger
                onClick={() => {
                    if (selectedTreatmentTemplate?.imageMediaFileId != null) {
                        dispatch(clearImage(selectedTreatmentTemplate.imageMediaFileId))
                    }
                }}
            >
                {t("actions.delete")}
            </Button>
        </Space>
        : <Button type="primary" onClick={() => fileRef.current?.click()}>{t("actions.add")}</Button>;

    const addForm = status === "adding" ? (
        <EditorPanel
            title={t(selectedTreatmentTemplate?.id != null ? "editor.edit" : "editor.new")}
            actions={
                <>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => dispatch(clearErrorTreatmentTemplate())}
                    >
                        {t("actions.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSave}
                        icon={<SaveOutlined />}
                        onClick={onSaveTreatmentTemplate}
                    >
                        {t("actions.save")}
                    </Button>
                </>
            }
        >
            <div className="treatment-editor">
                <Form name="basic" layout="vertical" autoComplete="off">
                    {/* One image serves every language, so it stays outside the locale tabs. */}
                    <LocalizedFields
                        fields={treatmentTemplateFields}
                        value={selectedTreatmentTemplate ?? {}}
                        onChange={template => dispatch(selectTreatmentTemplate(template))}
                    />
                </Form>

                <div className="treatment-editor__media">
                    <div className="treatment-editor__thumb">
                        <Image height={168} src={currentImage ?? "error"} fallback={IMAGE_FALLBACK} />
                    </div>
                    {changeImageButton}
                    <input
                        ref={fileRef}
                        accept=".jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        multiple={false}
                        type="file"
                        hidden
                    />
                </div>
            </div>
        </EditorPanel>
    ) : null;

    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            {status !== "adding" ? <CrudToolbar
                hasSelection={selectedTreatmentTemplate != null}
                canEdit={selectedTreatmentTemplate?.id != null}
                selectionLabel={selectedTreatmentTemplate?.name}
                onDelete={() => { if (selectedTreatmentTemplate != null) dispatch(startDeleteTreatmentTemplate()); }}
                onEdit={onEditTreatmentTemplate}
                onAdd={() => dispatch(startAddTreatmentTemplate({} as TreatmentTemplateResponseDto))}
                onReload={() => dispatch(fetchTreatmentTemplates())}
                onClearSelection={() => dispatch(selectTreatmentTemplate(undefined))}
            /> : null}
            {addForm}
            <div className="card card--flush">
                <Table
                    rowKey={(treatmentTemplate: TreatmentTemplateResponseDto) => treatmentTemplate.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={treatmentTemplates}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="treatment"
                deleteTarget={selectedTreatmentTemplate?.name}
                onDismiss={() => dispatch(clearErrorTreatmentTemplate())}
                onConfirmDelete={() => { if (selectedTreatmentTemplate != null) dispatch(deleteTreatmentTemplate(selectedTreatmentTemplate.id!)); }}
            />
        </>

    return (
        <>
            <PageHeader
                title={t("page.treatments.title")}
                count={treatmentTemplates?.length}
            />
            {content}
        </>
    );
}
